import { useEffect, useRef, useState } from 'react';
import { useUserActivity } from './useUserActivity';
import { refreshToken } from '../components/authService/refreshToken';
import { parseJwt } from '../helper/helpers';
import { logOut } from '../components/authService/auth';

// Refresh only during the last 5 minutes of the token's 30-minute lifespan
const REFRESH_WINDOW = 5 * 60 * 1000; // 5 minutes
const MAX_IDLE_ALLOWED = 25 * 60 * 1000; // 25 minutes
const CHECK_INTERVAL = 60 * 1000; // 1 minute

export const useTokenMonitor = () => {
  const { lastActiveTime } = useUserActivity();
  const tokenExpiryTime = useRef(null);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  useEffect(() => {
    if (isLoggedOut) return;

    const checkToken = async () => {
      if (isLoggedOut) return;

      const token = sessionStorage.getItem('token');
      if (!token) {
        console.warn('🚫 No token found, logging out.');
        setIsLoggedOut(true);
        logOut();
        return;
      }

      const decoded = parseJwt(token);
      if (!decoded?.exp) {
        console.warn('🚫 Invalid token format.');
        setIsLoggedOut(true);
        logOut();
        return;
      }

      const now = Date.now();
      const expiryTime = decoded.exp * 1000;
      const timeToExpiry = expiryTime - now;
      const idleTime = now - lastActiveTime;

      tokenExpiryTime.current = expiryTime;

      if (timeToExpiry <= 0) {
        console.warn('⏰ Token expired. Logging out.');
        setIsLoggedOut(true);
        logOut();
        return;
      }

      // Only check refresh when in the last 5 minutes of token life
      if (timeToExpiry <= REFRESH_WINDOW) {
        if (idleTime >= MAX_IDLE_ALLOWED) {
          console.warn('😴 User idle too long. Logging out.');
          setIsLoggedOut(true);
          logOut();
        } else {
          try {
            const response = await refreshToken();
            sessionStorage.setItem('token', response?.token);
            const newDecoded = parseJwt(response?.token);
            tokenExpiryTime.current = newDecoded.exp * 1000;
          } catch (err) {
            console.error('Token refresh failed:', err);
            setIsLoggedOut(true);
            logOut();
          }
        }
      }
    };

    const interval = setInterval(checkToken, CHECK_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [lastActiveTime, isLoggedOut]);
};
